import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { RefObject, useCallback, useLayoutEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import useTableSimpleEffectiveColumnPinning from "ds/components/TableSimple/hooks/useEffectiveColumnPinning";
import useTypedContext from "hooks/useTypedContext";
import { ModuleContext } from "views/Account/Module/Context";

import { ModulePullRequestsTableRow } from "../gql";
import { flattenModulePullRequestsTableRows } from "./helpers";
import ModulePullRequestsTableRowPullRequest from "./Row/PullRequest";
import ModulePullRequestsTableRowRun from "./Row/Run";
import ModulePullRequestsTableStickyParentRow from "./StickyParentRow";
import styles from "./styles.module.css";
import useModulePullRequestsTableExpandedRowsStore from "./useExpandedRowsStore";
import useModulePullRequestsTableStore from "./useTableStore";

type ModulePullRequestsTableRowsProps = {
  pullRequests: ModulePullRequestsTableRow[];
  tableRef: RefObject<HTMLDivElement | null>;
};

const ModulePullRequestsTableRows = ({
  pullRequests,
  tableRef,
}: ModulePullRequestsTableRowsProps) => {
  const { module } = useTypedContext(ModuleContext);
  const expandedRows = useModulePullRequestsTableExpandedRowsStore((state) => state.expandedRows);
  const groupKeyboardProps = useRowKeyboardNavigation({ isGroup: true });
  const rowKeyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = useModulePullRequestsTableStore(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const { rows, stickyRanges } = useMemo(
    () => flattenModulePullRequestsTableRows(pullRequests, expandedRows),
    [pullRequests, expandedRows]
  );

  const hasExpandableRows = rows.some((row) => row.kind === "parent" && row.isExpandable);

  const { rangeExtractor, stickyGroupsVisibleOnTableRef } = useStickyRangeExtractor(stickyRanges);

  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index];
      return row.kind === "parent" ? row.pullRequest.id : `${row.pullRequest.id}-${row.run.id}`;
    },
    [rows]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rows.length, virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateVirtualPadding(
    virtualizedItems,
    virtualizer.getTotalSize(),
    virtualizer.options.scrollMargin,
    stickyGroupsVisibleOnTableRef.current.size
  );

  const renderRow = (virtualRow: VirtualItem) => {
    const row = rows[virtualRow.index];

    if (row.kind === "child") {
      return (
        <ModulePullRequestsTableRowRun
          key={virtualRow.key}
          pullRequest={row.pullRequest}
          run={row.run}
          moduleId={module.id}
          moduleProvider={module.provider}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          keyboardProps={rowKeyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
        />
      );
    }

    // Render an expandable parent through the sticky row even while collapsed: swapping components
    // on toggle would remount the row and the chevron would lose focus.
    if (row.isExpandable) {
      const stickyGroupEnd = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
      const isActiveSticky = stickyGroupEnd !== undefined;
      const nextAfter = isActiveSticky
        ? virtualizer.measurementsCache[stickyGroupEnd + 1]
        : undefined;
      const nextAfterSticky = nextAfter
        ? nextAfter.start - (virtualizer.measurementsCache[virtualRow.index]?.size ?? 0) + 1
        : undefined;

      return (
        <ModulePullRequestsTableStickyParentRow
          key={virtualRow.key}
          pullRequest={row.pullRequest}
          moduleId={module.id}
          moduleProvider={module.provider}
          isExpanded={row.isExpanded}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          keyboardProps={groupKeyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          isActiveSticky={isActiveSticky}
          nextAfterSticky={nextAfterSticky}
          tableRef={tableRef}
        />
      );
    }

    return (
      <ModulePullRequestsTableRowPullRequest
        key={virtualRow.key}
        pullRequest={row.pullRequest}
        moduleId={module.id}
        moduleProvider={module.provider}
        columns={columns}
        isFirstColumnPinned={effectiveIsFirstColumnPinned}
        keyboardProps={rowKeyboardProps}
        measureRef={virtualizer.measureElement}
        dataIndex={virtualRow.index}
        className={hasExpandableRows ? styles.alignParent : undefined}
      />
    );
  };

  return (
    <>
      {topGap > 0 && <div style={{ display: "table-row", height: topGap }} aria-hidden="true" />}
      {virtualizedItems.map(renderRow)}
      {bottomGap > 0 && (
        <div style={{ display: "table-row", height: bottomGap }} aria-hidden="true" />
      )}
    </>
  );
};

export default ModulePullRequestsTableRows;
